import React, { useState } from 'react';
import {
  Chip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import CompressOutlinedIcon from '@mui/icons-material/CompressOutlined';
import ArcaPdfStudioLayout from '../../components/arca/ArcaPdfStudioLayout';
import { appendUploadFiles, isPdfFile } from '../../components/arca/uploadItemUtils';
import { getToken } from '../../api/client';
import API_CONFIG from '../../config/api';

function formatBytes(n) {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function baixarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const LEVEL_HELP = {
  low: 'Baixa — melhor qualidade',
  medium: 'Média — equilíbrio',
  high: 'Alta — menor arquivo',
};

export default function ArcaPdfComprimirPage() {
  const [items, setItems] = useState([]);
  const [level, setLevel] = useState('medium');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState(null);

  const file = items[0]?.file || null;

  const handleFilesAdd = (files) => {
    setError('');
    setStats(null);
    const n = appendUploadFiles(files, setItems, { multiple: false, filter: isPdfFile });
    if (!n) setError('Selecione um arquivo PDF.');
  };

  const handleCompress = async () => {
    setError('');
    setStats(null);
    if (!file) {
      setError('Envie um PDF primeiro.');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file, file.name);
      fd.append('level', level);
      const token = getToken();
      const res = await fetch(`${API_CONFIG.BACKEND_URL}/api/arca/pdf/compress`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Erro ao comprimir (${res.status}).`);
      }
      const originalSize = Number(res.headers.get('X-Arca-Pdf-Original-Size') || file.size);
      const compressedSize = Number(res.headers.get('X-Arca-Pdf-Compressed-Size') || 0);
      const usedOriginal = res.headers.get('X-Arca-Pdf-Used-Original') === '1';
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = match
        ? decodeURIComponent(match[1].replace(/"/g, ''))
        : `comprimido-${level}-${Date.now()}.pdf`;
      baixarBlob(blob, filename);
      const saved = Math.max(0, originalSize - compressedSize);
      const pct = originalSize ? Math.round((saved / originalSize) * 100) : 0;
      setStats({ originalSize, compressedSize, saved, pct, usedOriginal });
    } catch (e) {
      console.error('[ArcaPdfComprimir]', e);
      setError(e.message || 'Erro ao comprimir PDF.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ArcaPdfStudioLayout
      title="Comprimir PDF"
      info="Requer Ghostscript no servidor. Clique no thumbnail para visualizar · ↻ para girar antes de comprimir."
      limits={
        <>
          Limite: <strong>50 MB</strong> · 1 arquivo. Opcional: <code>GHOSTSCRIPT_PATH</code> no{' '}
          <code>backend/.env</code>.
        </>
      }
      error={error}
      onClearError={() => setError('')}
      onRotateError={setError}
      items={items}
      setItems={setItems}
      loading={loading}
      enableSortAlpha={false}
      dropzone={{
        accept: 'application/pdf,.pdf',
        multiple: false,
        maxSizeMb: 50,
        onFilesAdd: handleFilesAdd,
        label: 'Arraste um PDF ou clique para selecionar',
        hint: 'Máximo 50MB.',
      }}
      sidebarExtras={
        <>
          {file && (
            <FormControl disabled={loading} sx={{ mb: 2, width: '100%' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Nível de compressão
              </Typography>
              <RadioGroup value={level} onChange={(e) => setLevel(e.target.value)}>
                <FormControlLabel value="low" control={<Radio size="small" />} label={LEVEL_HELP.low} />
                <FormControlLabel
                  value="medium"
                  control={<Radio size="small" />}
                  label={LEVEL_HELP.medium}
                />
                <FormControlLabel
                  value="high"
                  control={<Radio size="small" />}
                  label={LEVEL_HELP.high}
                />
              </RadioGroup>
            </FormControl>
          )}
          {stats && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip size="small" label={`Antes: ${formatBytes(stats.originalSize)}`} />
              <Chip size="small" color="success" label={`Depois: ${formatBytes(stats.compressedSize)}`} />
              <Chip
                size="small"
                color={stats.pct > 0 ? 'primary' : 'default'}
                label={
                  stats.usedOriginal ? 'Sem redução útil' : `Economia: ${stats.pct}%`
                }
              />
            </Stack>
          )}
        </>
      }
      actionLabel="Comprimir e baixar"
      actionLoadingLabel="Comprimindo…"
      actionStartIcon={<CompressOutlinedIcon />}
      onAction={handleCompress}
      actionDisabled={!file}
      onClear={() => {
        setItems([]);
        setStats(null);
      }}
    />
  );
}
